import React from "react";
import { useTranslation } from "react-i18next";
import { useRemoveCartItemFromCart } from "../../hooks/useRemoveCartItemFromCart";
import { useUpdateCartItemQuantity } from "../../hooks/useUpdateCartItemQuantity";
import { useMoveToSavedForLaterFromCart } from "../../../savedForLater/hooks/useMoveToSavedForLaterFromCart";
import { useMoveCartItemToWishlist } from "../../hooks/useMoveCartItemToWishlist";
import { useToggleCompareItem } from "../../../compare/hooks/useToggleCompareItem";
import type { TransformedCartItem } from "../../types/cartApiTypes";
import QuantityControl from "../common/QuantityControl";
import CartItemActions from "../common/CartItemActions";
import { decodeHtmlEntities } from "../../../../../common/helpers/decodeHtmlEntities";

interface CartPanelItemProps {
  item: TransformedCartItem;
}

const CartPanelItem: React.FC<CartPanelItemProps> = ({ item }) => {
  const { t } = useTranslation();

  // --- Consume Custom Hooks ---
  const { quantity, handleQuantityChange, isUpdating } =
    useUpdateCartItemQuantity(item.key, item.quantity);

  const { handleRemoveCartItem: triggerRemove, isRemoving } =
    useRemoveCartItemFromCart();

  const { handleMoveCartItemToWishlist, isMoving } =
    useMoveCartItemToWishlist();

  const { handleMoveToSavedForLater, isMoving: isSaving } =
    useMoveToSavedForLaterFromCart();

  // Use item.id directly. For variations, this is the Variation ID.
  const { handleToggle: handleCompareToggle, isInCompare } =
    useToggleCompareItem(item.id);

  const onRemoveClick = () => {
    triggerRemove({ key: item.key });
  };

  const onMoveToWishlistClick = () => {
    handleMoveCartItemToWishlist(item.key);
  };

  const onSaveForLaterClick = () => {
    handleMoveToSavedForLater(item.key);
  };

  const onQuantityInputChange = (newQuantity: number) => {
    handleQuantityChange(newQuantity);
  };

  const image = item.images.length > 0 ? item.images[0] : null;
  const isLoading = isUpdating || isRemoving || isMoving || isSaving;

  const minQuantity = item.quantityLimits.minimum;
  const maxQuantity = item.quantityLimits.maximum;

  // Helper to ensure clean text for display
  const decodedName = decodeHtmlEntities(item.name);

  const isGift = item.rawPrice === "0";
  const promo = item.promotion;
  const firstOrder = item.firstOrderDiscount;

  return (
    <li className="cart-panel-item">
      {/* Primary Actions (Wishlist & Remove) */}
      <CartItemActions
        onMoveToWishlist={onMoveToWishlistClick}
        onRemove={onRemoveClick}
        isMoving={isMoving}
        isRemoving={isRemoving}
        itemName={decodedName}
        context="panel"
        placement="top"
      />

      {/* Wrapper for main content */}
      <div className="cart-panel-item__content">
        <div className="cart-panel-item__image">
          <a href={item.permalink}>
            {image ? (
              <img src={image.thumbnail} alt={image.alt || decodedName} />
            ) : (
              <div className="placeholder-image"></div>
            )}
          </a>
        </div>
        <div className="cart-panel-item__details">
          <a href={item.permalink} className="cart-panel-item__name">
            {decodedName}
          </a>

          {/* First Order Badge */}
          {firstOrder?.isEligible && (
            <span className="cart-item-promo-badge">
              {t("productCard.firstOrderBadge", {
                percentage: firstOrder.percentage,
                defaultValue: `İlk Siparişte %${firstOrder.percentage} İndirim`,
              })}
            </span>
          )}

          {/* Standard Promo Badge */}
          {promo?.isEligible && !firstOrder?.isEligible && (
            <span className="cart-item-promo-badge">
              {t("productCard.buyXPayYBadge", {
                buy: promo.buyQty,
                pay: promo.payQty,
                defaultValue: `Buy ${promo.buyQty} Pay ${promo.payQty}!`,
              })}
            </span>
          )}

          {item.variation.length > 0 && (
            <div className="cart-panel-item__meta">
              {item.variation.map((v) => (
                <div key={v.attribute}>
                  <strong>{v.attribute}:</strong> {decodeHtmlEntities(v.value)}
                </div>
              ))}
            </div>
          )}
          <div className="cart-panel-item__footer">
            <div className="cart-panel-item__footer-group">
              <span className="cart-panel-item__footer-label">
                {t("cartOffcanvas.item.price", "Price")}
              </span>
              <span className="cart-panel-item__price">
                {isGift ? (
                  <span className="is-gift">
                    {t("feedback.cart.gift", "Gift!")}
                  </span>
                ) : (
                  // Display the Catalog Price (Original Unit Price)
                  item.catalogPrice
                )}
              </span>
            </div>

            <div className="cart-panel-item__footer-group cart-panel-item__footer-group--quantity">
              <span className="cart-panel-item__footer-label">
                {t("cartOffcanvas.item.qty", "Qty")}
              </span>
              <QuantityControl
                value={quantity}
                onChange={onQuantityInputChange}
                min={minQuantity}
                max={maxQuantity}
                disabled={isLoading}
                ariaLabel={`Quantity for ${decodedName}`}
                className="cart-panel-item__quantity-controls"
              />
            </div>

            <div className="cart-panel-item__footer-group">
              <span className="cart-panel-item__footer-label">
                {t("cartOffcanvas.item.total", "Total")}
              </span>
              <span className="cart-panel-item__line-total">
                {isGift ? (
                  <span className="is-gift">
                    {t("feedback.cart.gift", "Gift!")}
                  </span>
                ) : (
                  item.formattedCalculatedLineTotal
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Actions (Save for Later & Compare) */}
      <CartItemActions
        onSaveForLater={onSaveForLaterClick}
        onToggleCompare={handleCompareToggle}
        isInCompare={isInCompare}
        isSaving={isSaving}
        itemName={decodedName}
        context="panel"
        placement="bottom"
      />
    </li>
  );
};

export default CartPanelItem;
